import { Component, State, h, Event, EventEmitter, Method, Prop } from '@stencil/core';
import {
  type ColumnType,
  type ApiDatoCelda,
  type CellMatrix,
  type EditableGridTransformed,
  type GridColumn,
  type GridRowDef,
} from '../../types/TablaDefTypes';
import { transformNumberCellToApiFormat } from '../../helpers/edGridApiSaveTransformer';

@Component({
  tag: 'editable-grid',
  styleUrl: 'editable-grid.css',
  shadow: true,
})
export class EditableGrid {
  @Prop() dataState?: 'loading' | 'loaded' | 'error' = 'loading';
  @State() parsedColumns: GridColumn[] = [];
  @State() parsedRows: GridRowDef[] = [];
  @State() parsedCells: CellMatrix = {};
  @State() dirtyCells: { [rowId: string]: { [colId: string]: boolean } } = {};

  private gridDataCopy: EditableGridTransformed = {
    cells: {},
    columns: [],
    rows: [],
  };

  @Method()
  async loadGridData(data: EditableGridTransformed) {
    this.newDataArrived(data);
  }

  private newDataArrived(data: EditableGridTransformed) {
    this.gridDataCopy = JSON.parse(JSON.stringify(data));

    this.parsedColumns = data.columns;
    this.parsedRows = data.rows;
    this.parsedCells = data.cells;
  }

  @Method()
  public async getAllGridChanges() {
    return this.getDirtyChanges();
  }

  private getDirtyChanges() {
    const changes: ApiDatoCelda[] = [];

    for (const [rowId, cols] of Object.entries(this.dirtyCells)) {
      for (const [colId, isDirty] of Object.entries(cols)) {
        if (isDirty) {
          const value = this.parsedCells[rowId]?.[colId];
          changes.push({
            fil_id: Number(rowId),
            col_id: Number(colId),
            wdato: value,
          });
        }
      }
    }

    return changes;
  }

  @Method()
  public async clearChangesNoSave() {
    this.clearChangedCells();
  }

  @Method()
  public async clearDirtyChanges() {
    this.clearLocalChanges();
  }

  private clearChangedCells() {
    this.parsedRows = JSON.parse(JSON.stringify(this.gridDataCopy.rows));
    this.parsedColumns = JSON.parse(JSON.stringify(this.gridDataCopy.columns));
    this.parsedCells = JSON.parse(JSON.stringify(this.gridDataCopy.cells));

    this.dirtyCells = {};
    this.cellChanged.emit(0);
  }

  private clearLocalChanges() {
    this.dirtyCells = {};
    this.cellChanged.emit(0);
  }

  /** Evento disparado al cambiar una celda */
  @Event({ eventName: 'cell-changed' })
  cellChanged!: EventEmitter<number>;

  // ================= LIFECYCLE =================

  componentWillLoad() {}
  componentWillRender() {}

  // ============== EDIT / SAVE ==============

  private validateInput(e: Event, colId: string) {
    const col = this.gridDataCopy.columns.find((c) => c.id === colId);

    if (!col) return;
    if (col.type === undefined) return;

    const input = e.target as HTMLInputElement;
    let value = input.value;

    if (col.type === 'number') {
      value = value.replace(/[^0-9.,]/, '');

      const parts = value.split(',');
      if (parts.length > 2) {
        value = parts[0] + ',' + parts.slice(1).join('');
      }

      const numArr: string[] = [];
      let [intPart = undefined, decPart = undefined] = parts;

      if (intPart !== undefined) {
        const dots = Array.from(intPart.matchAll(/[.]/g)).length;
        intPart = intPart.slice(0, (col.tablas_def_col_largo ?? Infinity) + dots);
        numArr.push(intPart);
      }

      if (decPart !== undefined) {
        decPart = decPart.slice(0, col.tablas_def_col_decimales ?? Infinity);

        numArr.push(decPart);
      }

      // console.table({ value, intPart, decPart: decPart === undefined, numArr, newValue: numArr.join(',') });
      value = numArr.join(',');
    }

    if (col.type === 'text') {
      value = value.substring(0, col.tablas_def_col_largo ?? Infinity);
    }

    if (col.type === 'date') {
    }

    input.value = value;
  }

  private handleCellInput(
    rowId: string,
    colId: string,
    input: HTMLInputElement,
    applyTransformersByType?: Partial<Record<ColumnType, (value: string) => string>> & { cellType: ColumnType }
  ) {
    if (!input) return;

    if (applyTransformersByType && applyTransformersByType.cellType) {
      const transformer = applyTransformersByType[applyTransformersByType.cellType] ?? ((v) => v);

      input.value = transformer(input.value);
    }

    const cellsClone: CellMatrix = { ...this.parsedCells };
    const rowCells = { ...(cellsClone[rowId] || {}) };

    rowCells[colId] = input.value;
    cellsClone[rowId] = rowCells;

    this.parsedCells = cellsClone;

    const dirtyClone = { ...this.dirtyCells };
    const rowDirty = { ...(dirtyClone[rowId] || {}) };
    rowDirty[colId] = true;
    dirtyClone[rowId] = rowDirty;
    this.dirtyCells = dirtyClone;

    /* const rowDef = this.parsedRows.find((r) => r.id === rowId);
    const colDef = this.parsedColumns.find((c) => c.id === colId);

    this.cellChanged.emit({
      currChange: {
        rowId,
        rowLabel: rowDef?.label ?? rowId,
        colId,
        colLabel: colDef?.label ?? colId,
        value,
        },
        allChanges: this.getDirtyChanges(),
        }); */

    this.cellChanged.emit(
      Object.values(this.dirtyCells).reduce((acc, row) => acc + Object.values(row).filter((v) => v).length, 0)
    );
  }

  private isRowDirty(rowId: string): boolean {
    const rowDirty = this.dirtyCells[rowId];
    if (!rowDirty) return false;
    return Object.values(rowDirty).some((v) => v);
  }

  // ============== RENDER ==============

  private renderCell(row: GridRowDef, col: GridColumn) {
    const value = this.parsedCells[row.id]?.[col.id] ?? '';

    const inputType =
      {
        date: 'date',
        number: 'text',
        text: 'text',
      }[col.type] ?? 'text';

    const inputmode = {
      date: 'numeric',
      number: 'numeric',
      text: undefined,
    }[col.type];

    return (
      <div class='grid-cell' key={`${row.id}-${col.id}-cell`}>
        <input
          key={`${row.id}-${col.id}-input`}
          id={`${row.id}-${col.id}-input`}
          type={inputType}
          value={value}
          onInput={(e) => this.validateInput(e, col.id)}
          onChange={(e) => {
            this.handleCellInput(row.id, col.id, e.target as HTMLInputElement, {
              cellType: col.type,
              number: transformNumberCellToApiFormat,
            });
          }}
          inputMode={inputmode}
        />
      </div>
    );
  }

  private renderSkeleton() {
    return [...Array.from({ length: 25 }).map((_, i) => <div class='skeleton-cell' key={`skeleton-${i}`}></div>), ,];
  }

  private renderInvalidGrid() {
    return (
      <div class='error-table-data'>
        <div class='message'>
          <svg viewBox='0 0 24 24' fill='none'>
            <path
              d='M2.20164 18.4695L10.1643 4.00506C10.9021 2.66498 13.0979 2.66498 13.8357 4.00506L21.7984 18.4695C22.4443 19.6428 21.4598 21 19.9627 21H4.0373C2.54022 21 1.55571 19.6428 2.20164 18.4695Z'
              stroke-width='2'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path d='M12 9V13' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' />
            <path d='M12 17.0195V17' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' />
          </svg>
          <p>Datos de tabla Invalidos</p>
        </div>
      </div>
    );
  }

  private renderGrid() {
    if (this.parsedColumns.length === 0) {
      return this.renderInvalidGrid();
    }

    const nroColumnas = Object.keys(this.parsedCells).length === 0 ? 5 : this.parsedColumns.length;

    return (
      <div
        class='tabla-def-grid'
        style={{
          'grid-template-columns': `minmax(max-content, 1fr) repeat(${nroColumnas}, minmax(max-content, .5fr))`,
        }}
      >
        <div class='empty-cell'></div>
        {...this.parsedColumns.map((col) => (
          <div class='column-title-cell' key={`${col.id}-title`}>
            {col.label}
          </div>
        ))}

        {...this.parsedRows.map((row) => [
          /** Primera celda de la fila es el titulo */
          <div class='row-title-cell' key={`${row.id}-title`}>
            {row.label}
          </div>,

          /** Celdas editables de la fila */
          ...this.parsedColumns.map((col) => this.renderCell(row, col)),
        ])}
      </div>
    );
  }

  private renderError() {
    return (
      <div class='error-table-data'>
        <div class='message'>
          <svg viewBox='0 0 24 24' fill='none'>
            <path
              d='M2.20164 18.4695L10.1643 4.00506C10.9021 2.66498 13.0979 2.66498 13.8357 4.00506L21.7984 18.4695C22.4443 19.6428 21.4598 21 19.9627 21H4.0373C2.54022 21 1.55571 19.6428 2.20164 18.4695Z'
              stroke-width='2'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path d='M12 9V13' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' />
            <path d='M12 17.0195V17' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' />
          </svg>
          <p>No se puede mostrar la tabla</p>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div class='editable-grid-container'>
        {this.dataState === 'error' ? this.renderError() : null}
        {this.dataState === 'loading' ? this.renderSkeleton() : null}
        {this.dataState === 'loaded' ? this.renderGrid() : null}
      </div>
    );
  }
}
